import { Box, Checkbox, TableCell, TableRow } from '@mui/material';
import { Add, Clear, FormatIndentIncrease } from '@mui/icons-material';
import { add, move, remove, update } from 'react-querybuilder';
import OutlinedButton from 'components/Button/OutlinedButton';
import {
	textfieldSxProps,
	defaultRuleValue,
	dropdownSxProps,
	ruleUnGroupOnRemove,
	ungroupRules
} from './utilities';
import CustomTextField from 'components/Inputs/CustomTextField';
import AsyncAutoComplete from './AsyncAutoComplete';
import { ReactComponent as DropdownBlueIcon } from '../../assets/dropdown.svg';
import CustomSelectDropdown from 'components/Dropdown/CustomSelectDropdown';
import { useTheme } from '@mui/material/styles';

const RuleRow = ({
	fields,
	rule,
	query,
	setQuery,
	combinator = null,
	path,
	combinatorPath,
	groupedRules,
	onCheckboxChange,
	isChecked,
	groupPaths,
	handleFieldChange,
	fieldValueOptionsCallback,
	handleOperatorChange,
	isProjectDetails
}: any) => {
	const theme = useTheme();
	const projectDetailsBoxPropsSx = isProjectDetails ? {
		borderRadius: '4px !important',
		backgroundColor: 'rgba(255, 255, 255, 0) !important'
	} : {};

	const projectDetailsMenuProps = isProjectDetails ? {
		PaperProps: {
			sx: {
				"&&&& .MuiMenuItem-root:hover": {
					background: '#003771 !important',
					color: '#FFFFFF !important',
				},
				"&&&& .MuiMenuItem-root.Mui-focusVisible": {
					background: '#003771 !important',
					color: '#FFFFFF !important',
				},
				"&&&& .MuiMenuItem-root.Mui-selected": {
					background: '#005092 !important',
					color: '#FFFFFF !important',
				},
				"&&&& .MuiMenuItem-root.Mui-selected:hover": {
					background: '#003771 !important',
					color: '#FFFFFF !important',
				}
			}
		}
	} : undefined;

	const projectDetailsSelectSx = isProjectDetails ? {
		background: "#EFF6FF !important",
		borderRadius: '4px !important',
		"& .MuiOutlinedInput-root": {
			borderRadius: '4px !important',
			backgroundColor: 'rgba(255, 255, 255, 0) !important'
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderRadius: '4px !important',
			backgroundColor: 'rgba(255, 255, 255, 0) !important'
		},
		"& .MuiSelect-select": {
			color: '#000000 !important',
			fontWeight: '500 !important'
		},
		"& .MuiBox-root": {
			fontWeight: '500 !important',
		},
		"& .MuiSelect-icon": {
			color: '#39ACE7 !important',
			fill: '#39ACE7 !important',
			top: 'calc(50% - 12px) !important'
		},
		"& .MuiSelect-icon path": {
			fill: '#39ACE7 !important'
		}
	} : {};

	const isFirstRule = path.at(-1) === 0;
	const groupPath = [...path].slice(0, -1);
	const groupPathString = groupPath.join('.');
	const maxGroupLength = Math.max(...groupPaths.current.map((path: any) => path.length));
	const handleChange = (field: any) => {
		handleFieldChange(field);
	};
	
	const handleOpChange = (operator: any) => {
		handleOperatorChange(operator, rule.field);
	};

	const renderValueInput = fields[rule.field]?.fieldOptions?.fieldInput === 'textfield' ? (
		<CustomTextField
			onChange={(e) => {
				const value = e.target.value;
				if (fields[rule.field]?.fieldOptions?.valueType === 'number') {
					// Allow only number inputs
					if (/^\d*\.?\d*$/.test(value)) {
						setQuery(update(query, 'value', Number(value), path));
					}
				} else {
					setQuery(update(query, 'value', value, path));
				}
			}}
			value={rule.value}
			sx={{
				...textfieldSxProps,
					background: `${theme.palette.DropdownV2.background} !important`,
					borderRadius: '4px !important',
					"& .MuiOutlinedInput-root": {
						borderRadius: '4px !important',
					},
					"& .MuiOutlinedInput-notchedOutline": {
						borderRadius: '4px !important',
					},
					"& .MuiInputBase-input": {
						color: `${theme.palette.DropdownV2.menuItemText} !important`,
						fontWeight: '500 !important',
					},
			}}
		/>
	) : (
		<AsyncAutoComplete
			query={query}
			fields={fields}
			path={path}
			rule={rule}
			setQuery={setQuery}
			fieldValueOptionsCallback={fieldValueOptionsCallback}
			isProjectDetails={isProjectDetails}
			sx={{
				background: `${theme.palette.DropdownV2.background} !important`,
				borderRadius: '4px !important',

				'& .MuiOutlinedInput-root': {
					height: '32px !important',
					backgroundColor: `${theme.palette.DropdownV2.background} !important`,
					borderRadius: '4px !important',
				},

				'& .MuiOutlinedInput-notchedOutline': {
					borderColor: 'transparent !important',
					borderRadius: '4px !important',
				},

				'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
					borderColor: `${theme.palette.DropdownV2.mutedText} !important`,
				},

				'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
					borderColor: `${theme.palette.DropdownV2.borderColorHover} !important`,
				},

				'& .MuiInputBase-input': {
					color: `${theme.palette.DropdownV2.menuItemText} !important`,
					fontWeight: '500 !important',
					fontSize: '14px',
					fontFamily: 'Montserrat',
				},

				'& .MuiAutocomplete-popupIndicator': {
					color: `${theme.palette.DropdownV2.dropdownIconDefault} !important`,
				},

				'& .MuiAutocomplete-clearIndicator': {
					color: `${theme.palette.DropdownV2.menuItemText} !important`,
				},

				'& .MuiAutocomplete-clearIndicator svg path': {
					fill: `${theme.palette.DropdownV2.menuItemText} !important`,
				},
			}}
			customTextFieldProps={{
				sx: {
					'& .MuiInputBase-root': {
						height: '32px !important',
						backgroundColor: `${theme.palette.DropdownV2.background} !important`,
						borderRadius: '4px !important',
					},
				},
			}}
			componentsProps={{
				paper: {
					sx: {
						borderRadius: '4px',
						backgroundColor: `${theme.palette.DropdownV2.menuItemBackground} !important`,
						color: `${theme.palette.DropdownV2.menuItemText} !important`,

						'&& .MuiAutocomplete-listbox': {
							paddingTop: '0 !important',
							paddingBottom: '0 !important',
						},

						'&& .MuiAutocomplete-option': {
							fontSize: '14px !important',
							fontFamily: 'Montserrat !important',
							color: `${theme.palette.DropdownV2.menuItemText} !important`,
							backgroundColor: 'transparent !important',

							'& .MuiTypography-root': {
								color: `${theme.palette.DropdownV2.menuItemText} !important`,
								fontSize: '14px !important',
								fontFamily: 'Montserrat !important',
								fontWeight: 'inherit !important',
							},
						},

						'&& .MuiAutocomplete-option:hover': {
							backgroundColor: `${theme.palette.DropdownV2.menuItemSelectedBg} !important`,
							color: `${theme.palette.DropdownV2.menuItemText} !important`,
						},

						'&& .MuiAutocomplete-option.Mui-focused': {
							backgroundColor: `${theme.palette.DropdownV2.menuItemSelectedBg} !important`,
							color: `${theme.palette.DropdownV2.menuItemText} !important`,
						},

						'&& .MuiAutocomplete-option.Mui-selected': {
							backgroundColor: `${theme.palette.DropdownV2.menuItemSelectedBg} !important`,
							color: `${theme.palette.DropdownV2.menuItemText} !important`,
							fontWeight: '500 !important',
						},

						'&& .MuiAutocomplete-option.Mui-selected .MuiTypography-root': {
							backgroundColor: `${theme.palette.DropdownV2.menuItemSelectedBg} !important`,
							color: `${theme.palette.DropdownV2.menuItemText} !important`,
							fontWeight: '500 !important',
						},

						'&& .MuiAutocomplete-option.Mui-selected:hover': {
							backgroundColor: `${theme.palette.DropdownV2.menuItemSelectedBg} !important`,
							color: `${theme.palette.DropdownV2.menuItemText} !important`,
						},

						'&& .MuiAutocomplete-option.Mui-selected.Mui-focused': {
							backgroundColor: `${theme.palette.DropdownV2.menuItemSelectedBg} !important`,
							color: `${theme.palette.DropdownV2.menuItemText} !important`,
						},

						'&& .MuiAutocomplete-option.Mui-selected.Mui-focused .MuiTypography-root': {
							backgroundColor: `${theme.palette.DropdownV2.menuItemSelectedBg} !important`,
							color: `${theme.palette.DropdownV2.menuItemText} !important`,
							fontWeight: '500 !important',
						},
					},
				},
			}}
		/>
	);

	return (
		<TableRow key={rule.id}>
			<TableCell align="left" sx={{bgcolor:"rgba(0, 0, 0, 0) !important"}}>
				<Box display="flex" justifyContent="left" gap={1}>
					<OutlinedButton disabled = {isFirstRule}
						sx={{ color: isFirstRule ? '#71758F' : 'error.main', 
							borderColor: isFirstRule ? '#71758F' : 'error.main', 
							width: 30, height: 30,
							'& .MuiSvgIcon-root': {
								color: isFirstRule ? '#71758F' : 'error.main',
							}, 
							}}
						onClick={() => {
							const newQuery = remove(query, path);
							setQuery(ruleUnGroupOnRemove(newQuery, groupedRules, path));
						}}
					>
						<Clear />
					</OutlinedButton>
					<OutlinedButton
						sx={{ color: 'info.main', borderColor: 'info.main', width: 30, height: 30 }}
						onClick={() => {
							const movePath = combinatorPath.length === 0 ? [0] : combinatorPath;
							const newQuery = add(query, defaultRuleValue, []);
							setQuery(move(newQuery, [newQuery.rules.length], movePath));
						}}
					>
						<Add sx={{color:'#39ACE7'}}/>
					</OutlinedButton>
				</Box>
			</TableCell>
			{!isProjectDetails && (
				<TableCell sx={{ background: `${theme.palette.queryBuilder.tableCellBackground} !important`}}>
					<Checkbox 
						sx={{
							color: `${theme.palette.table.checkBox} !important`,

							'&.Mui-checked': {
								color: `${theme.palette.table.checkBox} !important`,
							},

							'& .MuiSvgIcon-root': {
								color: `${theme.palette.table.checkBox} !important`,
							},
						}} 
						checked={isChecked} 
						onChange={() => onCheckboxChange(path)} 
					/>
				</TableCell>
			)}
			{!!groupPaths.current.length && (
				<TableCell sx={{ width: '1rem', background: `${theme.palette.queryBuilder.tableCellBackground} !important` }}>
					<Box display="flex" columnGap="1.2rem">
						{groupPaths.current.map((gPath: number[], idx: number) => {
							const pathString = gPath.join('.');
							const isGroup = groupPathString.startsWith(pathString);
							const isSameGroup = groupPathString === pathString;
							let isMultiNested = false;
							if (gPath.length < groupPath.length && isGroup) {
								const filledPath = [...gPath];
								while (filledPath.length < groupPath.length) {
									filledPath.push(0);
								}
								isMultiNested = filledPath.join('.') === groupPathString;
							}
							return isGroup && isFirstRule && (isSameGroup || isMultiNested) ? (
								<Box
									key={pathString}
									className="query-rule-row-head-icon"
									color="info.main"
									sx={{ cursor: 'pointer', backgroundColor: `${theme.palette.queryBuilder.tableCellBackground} !important` }}
									onClick={() => {
										setQuery(ungroupRules(query, groupPath));
									}}
									width={20}
								>
									<FormatIndentIncrease
										sx={{
											color: `${theme.palette.table.checkBox} !important`,
											fill: `${theme.palette.table.checkBox} !important`,

											'& path': {
												fill: `${theme.palette.table.checkBox} !important`,
											},
										}}
									/>
								</Box>
							) : isGroup && (
								<Box
									key={pathString}
									width={20}
									sx={{
										position: 'relative',
										// vertical line
										'&::before': {
											content: '""',
											position: 'absolute',
											left: '0.6rem',
											top: isFirstRule && isSameGroup ? '1.9rem' : '-3rem',
											bottom: 0,
											width: '0.14rem',
											backgroundColor: 'info.main'
										},
										...(path.length > 1 &&
											(!isFirstRule || (isFirstRule && !isMultiNested)) && {
												// horizontal line
												'&::after': {
													content: '""',
													position: 'absolute',
													left: `0.60rem`,
													top: '50%',
													height: '0.14rem',
													width: `${(maxGroupLength - gPath.length + 1) * 2.5}rem`,
													backgroundColor: 'info.main'
												}
											})
									}}
								/>
							)
						})}
					</Box>
				</TableCell>
			)}
			<TableCell sx={{ background: `${theme.palette.queryBuilder.tableCellBackground} !important` }}>
				<CustomSelectDropdown
					options={['and', 'or'].map((item) => ({
						value: item,
						label: item
					}))}
					handleChange={(e) => {
						setQuery(update(query, 'combinator', e.target.value, combinatorPath));
					}}
					value={combinator ?? ''}
					boxProps={{
						sx: {
						},
						...(!combinator && {
							visibility: 'hidden'
						})
					}}
					selectComponentProps={{
						sx: {
							height: '32px !important',
						},
						disabled: !combinator
					}}
				/>
			</TableCell>

			<TableCell sx={{ background: `${theme.palette.queryBuilder.tableCellBackground} !important` }}>
				<CustomSelectDropdown
					options={Object.values(fields).map((item: any) => ({
						value: item.name,
						label: item.label
					}))}
					handleChange={(e) => {
						handleChange(e.target.value);
						setQuery(update(query, 'field', e.target.value, path));
					}}
					value={rule.field}
					selectComponentProps={{
						sx: {
							height: '32px !important',
							...(!isProjectDetails && {
								"& .MuiSelect-select": {
									color:  `${theme.palette.DropdownV2.menuItemText} !important`,
								},
								"& .MuiBox-root": {
									fontWeight: '500 !important',
								},
							})
						},
					}}
				/>
			</TableCell>

			<TableCell sx={{ background: `${theme.palette.queryBuilder.tableCellBackground} !important` }}>
				<CustomSelectDropdown
					options={(fields[rule.field]?.fieldOptions?.operators ?? []).map((item: { value: string; label: string }) => ({
						value: item.value,
						label: item.label
					}))}
					handleChange={(e) => {
						handleOpChange(e.target.value);

						const newOperator = e.target.value;
						let updatedQuery = update(query, 'operator', newOperator, path);
						if (newOperator === 'null' || newOperator === 'notNull') {
							updatedQuery = update(updatedQuery, 'value', '', path); 
						}
						setQuery(updatedQuery);
					}}
					value={rule.operator}
					selectComponentProps={{
						sx: {
							height: '32px !important',
						},
					}}
				/>
			</TableCell>

			<TableCell sx={{ background: `${theme.palette.queryBuilder.tableCellBackground} !important` }}>
				{/* conditional rendering of input field based on the input type */}
				{fields[rule.field]?.fieldOptions?.valueType === 'boolean' ? (
					<CustomSelectDropdown
						options={[
							{
								value: 'false',
								label: 'False'
							},
							{
								value: 'true',
								label: 'True'
							}
						]}
						handleChange={(e) => {
							const booleanValue = e.target.value === 'true';
							setQuery(update(query, 'value', booleanValue, path));
						}}
						value={String(rule.value)}
						selectComponentProps={{
							displayEmpty: true,
							sx: {
								height: '32px !important',
							},
						}}
					/>
				) : renderValueInput}
			</TableCell>

		</TableRow>
	);
};

export default RuleRow;
